import { useState } from 'react';
import { Building2, Home, Store, Trees, Warehouse } from 'lucide-react';
import { Image } from '@/shared/ui';
import { normalizeImageUrl } from '@/shared/utils/normalize-image-url';
import { getCategoryAccentColor } from './category-accent';
import type { PropertyResponseType } from '@services/housien/types';

/** Category words that pick each silhouette, tried in order; anything else gets the house. */
const CATEGORY_ICON_RULES: ReadonlyArray<{ words: readonly string[]; icon: typeof Home }> = [
  { words: ['apart', 'flat', 'kit'], icon: Building2 },
  { words: ['terreno', 'lote', 'rural', 'chácara'], icon: Trees },
  { words: ['galp', 'barrac'], icon: Warehouse },
  { words: ['loja', 'sala', 'comerc'], icon: Store },
];

/**
 * Picks the silhouette that stands in for a missing cover photo.
 *
 * @param category - Raw or localized category value.
 * @returns The lucide icon component for that category.
 */
function categoryIcon(category: string | null | undefined) {
  const value = (category ?? '').toLowerCase();
  const rule = CATEGORY_ICON_RULES.find(({ words }) => words.some((word) => value.includes(word)));
  return rule ? rule.icon : Home;
}

/**
 * Cover photo preview for a property row.
 *
 * The URL rides along with the page the list already fetched
 * (`media.cover_thumbnail`, a 160px WebP derivative the backend caches), so a
 * page of rows costs no extra requests and a few KB rather than the full-size
 * uploads.
 *
 * The default box is the 72px square the detail-side callers use; the list
 * passes 64 × 48 because 4:3 is the shape a listing photo is actually taken
 * in, so the crop stops cutting the house in half.
 *
 * When the listing has no photo the box stays, filled with the category
 * silhouette tinted in that category's accent. An *empty* box would read as a
 * photo that failed to load, but a silhouette reads as what it is — no cover
 * yet — and keeps every row the same height, which is the whole point of a
 * fixed-height list.
 *
 * @param props.property - The listing row.
 * @param props.size - Square side of the preview, in pixels.
 * @param props.width - Explicit width, overriding `size`.
 * @param props.height - Explicit height, overriding `size`.
 * @param props.category - Category used to pick and tint the placeholder.
 * @returns The thumbnail element.
 */
export function PropertyThumbnail(props: {
  property: PropertyResponseType;
  size?: number;
  width?: number;
  height?: number;
  category?: string | null;
}) {
  const { property, size = 72, width = size, height = size, category } = props;
  const [failed, setFailed] = useState(false);

  const url = (property.media as { cover_thumbnail?: string } | undefined)?.cover_thumbnail;

  const box: React.CSSProperties = {
    width,
    height,
    flexShrink: 0,
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--surface-secondary)',
  };

  if (!url || failed) {
    const Icon = categoryIcon(category);
    return (
      <div
        aria-hidden="true"
        style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', color: getCategoryAccentColor(category), opacity: 0.75 }}
      >
        <Icon width={20} height={20} strokeWidth={1.6} />
      </div>
    );
  }

  return (
    <Image
      src={normalizeImageUrl(url)}
      alt=""
      aria-hidden="true"
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ ...box, objectFit: 'cover' }}
    />
  );
}
